var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var _a, _b;
const CLOUD_CONVERT_EXTS = ['ai', 'eps', 'pdf', 'svg'];
const IMAGE_EXTS = ['jpg', 'jpeg', 'png', 'webp'];
const VECTOR_MAX_SIZE = 2048;
const FALLBACK_PIXEL_DENSITY = 600;
const jobTag = `customizer_${typeof window !== 'undefined' ? (_b = (_a = window.location) === null || _a === void 0 ? void 0 : _a.hostname) !== null && _b !== void 0 ? _b : 'localhost' : 'localhost'}`;
const createJobUrl = 'https://sync.api.cloudconvert.com/v2/jobs';
/**
 * Create image element from blob.
 * @param blob - The image blob to create the element from.
 * @returns A promise that resolves to the created image element.
 */
export const createFromBlob = (blob) => {
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(blob);
        const img = new Image();
        img.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error('Failed to load image'));
        };
        img.onload = () => {
            URL.revokeObjectURL(url);
            resolve(img);
        };
        img.src = url;
    });
};
/**
 * Convert vector images (ai, eps, pdf, svg) to PNG via CloudConvert.
 * @param file - Original File.
 * @param apiKey - CloudConvert API key.
 * @returns A promise that resolves to the PNG image URL.
 */
export const vectorToPNG = (file, apiKey) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.addEventListener('load', () => __awaiter(void 0, void 0, void 0, function* () {
            var _a, _b;
            const base64Data = reader.result.split('base64,').pop();
            const ext = (_b = (_a = file.name.split('.').pop()) === null || _a === void 0 ? void 0 : _a.toLowerCase()) !== null && _b !== void 0 ? _b : '';
            const isVector = ['eps', 'svg'].includes(ext);
            const convertBodyParams = {
                operation: 'convert',
                input_format: ext,
                output_format: 'png',
                input: ['import'],
                alpha: true,
                text_to_path: true,
            };
            if (isVector) {
                const size = yield getVectorSize(file, ext);
                if (size) {
                    const scaledSize = scaleToMax(size, VECTOR_MAX_SIZE);
                    convertBodyParams.width = scaledSize.width;
                    convertBodyParams.height = scaledSize.height;
                }
                else {
                    convertBodyParams.pixel_density = FALLBACK_PIXEL_DENSITY;
                }
            }
            const body = {
                tasks: {
                    import: {
                        operation: 'import/base64',
                        file: base64Data,
                        filename: file.name,
                    },
                    conversion_task: Object.assign({}, convertBodyParams),
                    export: {
                        operation: 'export/url',
                        input: ['conversion_task'],
                        inline: true,
                        archive_multiple_files: false,
                    },
                },
                tag: jobTag,
            };
            try {
                const response = yield fetch(createJobUrl, {
                    method: 'POST',
                    headers: {
                        Accept: 'application/json',
                        Authorization: `Bearer ${apiKey}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(body),
                });
                const { status } = response;
                if (status < 200 || status >= 400) {
                    throw new Error(status.toString());
                }
                const job = (yield response.json()).data;
                const exportTask = job.tasks.find((task) => task.name === 'export');
                const imageUrl = exportTask.result.files[0].url;
                resolve(imageUrl);
            }
            catch (err) {
                console.error(err);
                reject(err);
            }
        }), false);
        reader.readAsDataURL(file);
    });
};
function getVectorSize(file, ext) {
    return __awaiter(this, void 0, void 0, function* () {
        switch (ext) {
            case 'svg':
                return yield getSvgSize(file);
            case 'eps':
                return yield getEpsSize(file);
            default:
                return null;
        }
    });
}
function getEpsSize(file) {
    return __awaiter(this, void 0, void 0, function* () {
        const text = yield file.text();
        const match = text.match(/%%BoundingBox:\s*([\d.-]+)\s+([\d.-]+)\s+([\d.-]+)\s+([\d.-]+)/);
        if (match) {
            const llx = parseFloat(match[1]);
            const lly = parseFloat(match[2]);
            const urx = parseFloat(match[3]);
            const ury = parseFloat(match[4]);
            return { width: urx - llx, height: ury - lly };
        }
        return null;
    });
}
function getSvgSize(file) {
    return __awaiter(this, void 0, void 0, function* () {
        const text = yield file.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(text, 'image/svg+xml');
        const svg = doc.documentElement;
        let width = svg.getAttribute('width');
        let height = svg.getAttribute('height');
        if ((!width || !height) && svg.hasAttribute('viewBox')) {
            const viewBox = svg.getAttribute('viewBox');
            const [, , vbWidth, vbHeight] = viewBox.split(/\s+/).map(Number);
            width = vbWidth.toString();
            height = vbHeight.toString();
        }
        if (width && height) {
            return { width: parseFloat(width), height: parseFloat(height) };
        }
        return null;
    });
}
function scaleToMax(size, maxSide) {
    const { width, height } = size;
    const scale = maxSide / Math.max(width, height);
    return {
        width: Math.round(width * scale),
        height: Math.round(height * scale),
    };
}
const getImageSizeFromUrl = (url) => new Promise(resolve => {
    const img = new Image();
    img.onload = () => {
        resolve({ x: img.naturalWidth, y: img.naturalHeight });
    };
    img.onerror = () => {
        resolve({ x: 100, y: 100 });
    };
    img.src = url;
});
/**
 * Check if file extension requires CloudConvert for conversion.
 */
export const needsCloudConvert = (fileName) => {
    var _a;
    const ext = (_a = fileName.split('.').pop()) === null || _a === void 0 ? void 0 : _a.toLowerCase();
    return ext ? CLOUD_CONVERT_EXTS.includes(ext) : false;
};
/**
 * Convert a file to image URL and size. Handles both raster images and vector formats.
 * - IMAGE_EXTS (jpg, jpeg, png, webp): Uses createFromBlob, returns object URL.
 * - CLOUD_CONVERT_EXTS (ai, eps, pdf, svg): Uses CloudConvert to convert to PNG.
 */
export const fileToImageUrl = (file, cloudConvertApiKey) => __awaiter(void 0, void 0, void 0, function* () {
    var _a;
    const ext = (_a = file.name.split('.').pop()) === null || _a === void 0 ? void 0 : _a.toLowerCase();
    if (!ext) {
        throw new Error('File has no extension');
    }
    if (IMAGE_EXTS.includes(ext)) {
        const img = yield createFromBlob(file);
        const imageSize = { x: img.naturalWidth, y: img.naturalHeight };
        const imageSrc = URL.createObjectURL(file);
        return {
            imageSrc,
            imageSize,
            revokeUrl: () => URL.revokeObjectURL(imageSrc),
        };
    }
    if (CLOUD_CONVERT_EXTS.includes(ext)) {
        if (!cloudConvertApiKey) {
            throw new Error('CLOUD_CONVERT_API_KEY is required for vector files (ai, eps, pdf, svg). Add it to your .env file.');
        }
        const imageSrc = yield vectorToPNG(file, cloudConvertApiKey);
        const imageSize = yield getImageSizeFromUrl(imageSrc);
        return { imageSrc, imageSize };
    }
    throw new Error(`Unsupported file extension: ${ext}`);
});
export const CLOUD_CONVERT_EXTS_LIST = CLOUD_CONVERT_EXTS;
export const IMAGE_EXTS_LIST = IMAGE_EXTS;
